import DataFileMap from '@/model/Resource/DataFileMap'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import {
  buildDefaultVirtualResourceRoot,
  syncVirtualResourcePathMetadata,
} from '@/features/virtual-resource/rules'

const MATERIAL_SOURCE_LINK_INDEX = 1
const MATERIAL_COMPOSITE_SIGNATURE_LINK_INDEX = 3
const RESOURCE_MD5_LINK_INDEX = 6
const RESOURCE_SIZE_LINK_INDEX = 7

/** 过滤非法文件名字符，避免写入资源树后出现不可用节点。 */
function sanitizeFileName(name: string): string {
  return name.trim().replace(/[\\/:*?"<>|]/g, '_')
}

/** 拆分文件基础名和扩展名，便于生成不冲突的重名后缀。 */
function splitBaseAndExt(name: string): { base: string; ext: string } {
  const match = /^(.*?)(\.[^.]+)?$/.exec(name.trim())
  return {
    base: match?.[1] || name,
    ext: match?.[2] || '',
  }
}

/** 在目标文件夹内生成唯一文件名。 */
function uniqueFileName(folder: DataFileMap, fileName: string): string {
  const cleanName = sanitizeFileName(fileName) || '素材'
  if (!folder.children.some((item) => !item.isFolder && item.name === cleanName)) {
    return cleanName
  }

  const { base, ext } = splitBaseAndExt(cleanName)
  let index = 2
  let nextName = `${base} (${index})${ext}`
  while (folder.children.some((item) => !item.isFolder && item.name === nextName)) {
    index += 1
    nextName = `${base} (${index})${ext}`
  }
  return nextName
}

/** 确保目标目录链存在，不存在则按层级补建。 */
function ensureFolder(root: DataFileMap, segments: string[]): DataFileMap {
  let current = root
  for (const segment of segments.filter(Boolean)) {
    let next = current.children.find((item) => item.isFolder && item.name === segment) ?? null
    if (!next) {
      next = DataFileMap.categoryRoot(segment)
      current.children.push(next)
    }
    current = next
  }
  return current
}

function applyResourceMeta(node: DataFileMap, md5?: string, size?: number): void {
  if (md5?.trim()) node.links[RESOURCE_MD5_LINK_INDEX] = md5.trim().toLowerCase()
  if (Number.isFinite(size) && Number(size) > 0) {
    node.links[RESOURCE_SIZE_LINK_INDEX] = String(Math.floor(Number(size)))
  }
}

/** 读取虚拟资源节点中保存的素材库原始下载地址。 */
function getMaterialSourceUrl(node: DataFileMap): string {
  return node.links[MATERIAL_SOURCE_LINK_INDEX]?.trim() ?? ''
}

/** 读取虚拟资源节点中保存的组合素材签名。 */
function getMaterialCompositeSignature(node: DataFileMap): string {
  return node.links[MATERIAL_COMPOSITE_SIGNATURE_LINK_INDEX]?.trim() ?? ''
}

/**
 * 在虚拟资源树里按素材源地址查找已导入节点。
 * 返回值中的 `folderSegments` 表示文件所在相对目录，便于资源管理器直接定位。
 */
export function findMaterialResourceMappingBySourceUrl(
  root: DataFileMap | null,
  sourceUrl: string,
): { node: DataFileMap; folderSegments: string[] } | null {
  const normalized = sourceUrl.trim()
  if (!root || !normalized) return null

  function walk(node: DataFileMap, folderSegments: string[]): { node: DataFileMap; folderSegments: string[] } | null {
    if (!node.isFolder && getMaterialSourceUrl(node) === normalized) {
      return { node, folderSegments }
    }
    for (const child of node.children) {
      const nextSegments = child.isFolder ? [...folderSegments, child.name] : folderSegments
      const hit = walk(child, nextSegments)
      if (hit) return hit
    }
    return null
  }

  return walk(root, [])
}

/** 在虚拟资源树里按组合签名查找已导入节点。 */
export function findMaterialResourceMappingByCompositeSignature(
  root: DataFileMap | null,
  signature: string,
): { node: DataFileMap; folderSegments: string[] } | null {
  const normalized = signature.trim()
  if (!root || !normalized) return null

  function walk(node: DataFileMap, folderSegments: string[]): { node: DataFileMap; folderSegments: string[] } | null {
    if (!node.isFolder && getMaterialCompositeSignature(node) === normalized) {
      return { node, folderSegments }
    }
    for (const child of node.children) {
      const nextSegments = child.isFolder ? [...folderSegments, child.name] : folderSegments
      const hit = walk(child, nextSegments)
      if (hit) return hit
    }
    return null
  }

  return walk(root, [])
}

/** 判断指定素材源地址是否已经导入过当前分类的虚拟资源树。 */
export function hasMaterialResourceMappingBySourceUrl(root: DataFileMap | null, sourceUrl: string): boolean {
  return !!findMaterialResourceMappingBySourceUrl(root, sourceUrl)
}

/** 判断指定组合签名是否已经导入过当前分类的虚拟资源树。 */
export function hasMaterialResourceMappingByCompositeSignature(root: DataFileMap | null, signature: string): boolean {
  return !!findMaterialResourceMappingByCompositeSignature(root, signature)
}

/**
 * 向虚拟资源树写入一条素材资源，并返回可能被自动改名后的文件名。
 * 这个返回值用于 UI 素材导入时回写引用路径。
 */
export function appendMaterialResourceMappingEntry(params: {
  root: DataFileMap | null
  category: ResourceCategoryKey
  folderSegments: string[]
  fileName: string
  url: string
  sourceUrl?: string
  md5?: string
  size?: number
  extraLinks?: Record<number, string>
  replaceSameName?: boolean
}): { root: DataFileMap; fileName: string } {
  const root = params.root
    ? new DataFileMap(params.root.toJson())
    : buildDefaultVirtualResourceRoot(params.category)
  const existingBySource = params.sourceUrl ? findMaterialResourceMappingBySourceUrl(root, params.sourceUrl) : null
  if (existingBySource) {
    applyResourceMeta(existingBySource.node, params.md5, params.size)
    syncVirtualResourcePathMetadata(params.category, root)
    return { root, fileName: existingBySource.node.name }
  }
  const folder = ensureFolder(root, params.folderSegments)
  const cleanFileName = sanitizeFileName(params.fileName) || '素材'

  if (params.replaceSameName) {
    const existingByNameIndex = folder.children.findIndex((item) => !item.isFolder && item.name === cleanFileName)
    if (existingByNameIndex >= 0) {
      const leaf = DataFileMap.fileLeaf(cleanFileName, params.url)
      if (params.sourceUrl?.trim()) {
        leaf.links[MATERIAL_SOURCE_LINK_INDEX] = params.sourceUrl.trim()
      }
      applyResourceMeta(leaf, params.md5, params.size)
      for (const [indexText, value] of Object.entries(params.extraLinks ?? {})) {
        const index = Number(indexText)
        const normalized = value?.trim()
        if (!Number.isInteger(index) || index < 0 || !normalized) continue
        leaf.links[index] = normalized
      }
      folder.children.splice(existingByNameIndex, 1, leaf)
      syncVirtualResourcePathMetadata(params.category, root)
      return { root, fileName: cleanFileName }
    }
  }

  const existing = folder.children.find(
    (item) => !item.isFolder && item.resourceUrl === params.url && item.name === params.fileName,
  )
  if (existing) {
    applyResourceMeta(existing, params.md5, params.size)
    syncVirtualResourcePathMetadata(params.category, root)
    return { root, fileName: existing.name }
  }

  const resolvedFileName = uniqueFileName(folder, cleanFileName)
  const leaf = DataFileMap.fileLeaf(resolvedFileName, params.url)
  if (params.sourceUrl?.trim()) {
    leaf.links[MATERIAL_SOURCE_LINK_INDEX] = params.sourceUrl.trim()
  }
  applyResourceMeta(leaf, params.md5, params.size)
  for (const [indexText, value] of Object.entries(params.extraLinks ?? {})) {
    const index = Number(indexText)
    const normalized = value?.trim()
    if (!Number.isInteger(index) || index < 0 || !normalized) continue
    leaf.links[index] = normalized
  }
  folder.children.push(leaf)
  syncVirtualResourcePathMetadata(params.category, root)
  return { root, fileName: resolvedFileName }
}

/** 不关心最终文件名时的便捷包装。 */
export function appendMaterialResourceMapping(params: {
  root: DataFileMap | null
  category: ResourceCategoryKey
  folderSegments: string[]
  fileName: string
  url: string
  sourceUrl?: string
  md5?: string
  size?: number
  extraLinks?: Record<number, string>
}): DataFileMap {
  return appendMaterialResourceMappingEntry(params).root
}
